const GRID_SIZE = 6;
const SUN = 1;
const CLOUD = 0;

// Simple string hash function for deterministic seeding
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
}

// Create a deterministic seed from a date string
function createSeedFromDate(dateString) {
  return hashString(dateString);
}

// Seeded random number generator
class SeededRandom {
  constructor(seed) {
    this.seed = seed;
  }

  next() {
    const x = Math.sin(this.seed++) * 10000;
    return x - Math.floor(x);
  }
}

// Check if a grid has consecutive suns
function hasConsecutiveSuns(grid, row, col) {
  if (grid[row][col] !== SUN) return false;

  const directions = [
    [0, 1], // right
    [1, 0], // down
    [1, 1], // down-right
    [-1, 1], // up-right
  ];

  for (const [dx, dy] of directions) {
    let consecutiveCount = 1;
    let r = row;
    let c = col;

    // Check in both directions
    for (let i = 0; i < 2; i++) {
      let currentDx = i === 0 ? dx : -dx;
      let currentDy = i === 0 ? dy : -dy;
      r = row + currentDx;
      c = col + currentDy;

      while (r >= 0 && r < GRID_SIZE && c >= 0 && c < GRID_SIZE) {
        if (grid[r][c] === SUN) {
          consecutiveCount++;
          if (consecutiveCount >= 3) return true;
        } else {
          break;
        }
        r += currentDx;
        c += currentDy;
      }
    }
  }
  return false;
}

// Generate a valid puzzle using a more efficient algorithm
function generateValidPuzzle(random) {
  // Start with an empty grid
  const grid = Array(GRID_SIZE)
    .fill()
    .map(() => Array(GRID_SIZE).fill(CLOUD));

  // Create a list of all positions
  const positions = [];
  for (let i = 0; i < GRID_SIZE; i++) {
    for (let j = 0; j < GRID_SIZE; j++) {
      positions.push([i, j]);
    }
  }

  // Shuffle positions
  for (let i = positions.length - 1; i > 0; i--) {
    const j = Math.floor(random.next() * (i + 1));
    [positions[i], positions[j]] = [positions[j], positions[i]];
  }

  // Place suns ensuring no more than 2 consecutive
  let placedSuns = 0;
  // Adjust max suns for 6x6 grid - make it slightly more challenging
  const maxSuns = Math.floor(GRID_SIZE * GRID_SIZE * 0.4); // Increased from 0.35 to 0.4

  // Try to ensure minimum suns for challenge
  const minSuns = Math.floor(GRID_SIZE * GRID_SIZE * 0.25);

  for (const [row, col] of positions) {
    if (placedSuns >= maxSuns) break;

    // If we haven't reached minimum suns, be more aggressive in placing them
    const shouldTryPlace =
      placedSuns < minSuns
        ? random.next() < 0.7 // 70% chance if below minimum
        : random.next() < 0.5; // 50% chance otherwise

    if (shouldTryPlace) {
      // Try placing a sun
      grid[row][col] = SUN;

      // Check if this placement creates consecutive suns
      if (hasConsecutiveSuns(grid, row, col)) {
        // If it does, revert the placement
        grid[row][col] = CLOUD;
      } else {
        placedSuns++;
      }
    }
  }

  // Calculate row and column sums
  const rowSums = grid.map((row) => row.reduce((sum, cell) => sum + cell, 0));
  const colSums = Array(GRID_SIZE).fill(0);
  for (let i = 0; i < GRID_SIZE; i++) {
    for (let j = 0; j < GRID_SIZE; j++) {
      colSums[j] += grid[i][j];
    }
  }

  // Ensure puzzle has enough constraints to be challenging
  const totalSuns = placedSuns;
  const avgPerLine = totalSuns / GRID_SIZE;
  const hasGoodDistribution =
    rowSums.some((sum) => sum >= avgPerLine + 1) &&
    colSums.some((sum) => sum >= avgPerLine + 1);

  // If distribution isn't challenging enough, generate a new puzzle
  if (!hasGoodDistribution || totalSuns < minSuns) {
    return generateValidPuzzle(random);
  }

  return { grid, rowSums, colSums };
}

// Generate a daily puzzle
export function generateDailyPuzzle(dateString) {
  const seed = createSeedFromDate(dateString);
  const random = new SeededRandom(seed);

  // Generate a valid puzzle
  const { grid, rowSums, colSums } = generateValidPuzzle(random);

  return {
    solution: grid,
    rowConstraints: rowSums,
    colConstraints: colSums,
    difficulty: 1, // Simple difficulty rating for now
  };
}
